import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { translations } from "./translations";

export type Lang = "EN" | "FI";

type Ctx = { lang: Lang; setLang: (l: Lang) => void };
const LangCtx = createContext<Ctx>({ lang: "EN", setLang: () => {} });

export function useLang() {
  return useContext(LangCtx);
}

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA"]);
const TRANSLATE_ATTRS = ["placeholder", "aria-label", "alt", "title"] as const;

function buildLookup(dict: Record<string, string>) {
  const lower: Record<string, string> = {};
  for (const k in dict) lower[k.toLowerCase()] = dict[k];
  return lower;
}

function lookup(raw: string, dict: Record<string, string>, lower: Record<string, string>) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return (
    dict[trimmed] ??
    dict[raw] ??
    lower[trimmed.toLowerCase()] ??
    null
  );
}

function translateNode(
  node: Node,
  dict: Record<string, string>,
  lower: Record<string, string>,
) {
  if (node.nodeType === Node.TEXT_NODE) {
    const raw = node.textContent ?? "";
    const hit = lookup(raw, dict, lower);
    if (hit) {
      const leading = raw.match(/^\s*/)?.[0] ?? "";
      const trailing = raw.match(/\s*$/)?.[0] ?? "";
      const next = leading + hit + trailing;
      if (node.textContent !== next) node.textContent = next;
    }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (SKIP_TAGS.has(el.tagName)) return;
  for (const attr of TRANSLATE_ATTRS) {
    const v = el.getAttribute(attr);
    if (v) {
      const t = lookup(v, dict, lower);
      if (t && t !== v) el.setAttribute(attr, t);
    }
  }
  el.childNodes.forEach((c) => translateNode(c, dict, lower));
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Always start with "EN" so SSR and the first client render match.
  // We promote to the stored language inside an effect after hydration.
  const [lang, setLangState] = useState<Lang>("EN");

  useEffect(() => {
    const s = window.localStorage.getItem("lang");
    if (s === "FI" || s === "EN") {
      setLangState(s);
      return;
    }
    // First visit: detect language from browser + IP geolocation.
    const browserLang = navigator.language?.toLowerCase() ?? "";
    if (browserLang.startsWith("fi")) {
      setLangState("FI");
      window.localStorage.setItem("lang", "FI");
      return;
    }
    // Fallback: IP-based country lookup (free, no key).
    const controller = new AbortController();
    fetch("https://ipapi.co/country/", { signal: controller.signal })
      .then((r) => (r.ok ? r.text() : ""))
      .then((country) => {
        if (country.trim().toUpperCase() === "FI") {
          setLangState("FI");
          window.localStorage.setItem("lang", "FI");
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);


  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang.toLowerCase();
    }
    if (lang !== "FI") return;
    const dict = translations.FI;
    const lower = buildLookup(dict);

    const run = () => translateNode(document.body, dict, lower);
    run();

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData") {
          translateNode(m.target, dict, lower);
        } else {
          m.addedNodes.forEach((n) => translateNode(n, dict, lower));
        }
      }
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => obs.disconnect();
  }, [lang]);

  const setLang = (l: Lang) => {
    if (typeof window !== "undefined") window.localStorage.setItem("lang", l);
    if (l === "EN" && lang === "FI" && typeof window !== "undefined") {
      // Restore originals by reloading — simplest robust path.
      window.location.reload();
      return;
    }
    setLangState(l);
  };

  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}
